import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Radar, Lightbulb, AlertTriangle, TrendingUp, Users, ExternalLink,
  Loader2, Info, Clock
} from "lucide-react";
import type { HarryFinding } from "@shared/schema";
import { format } from "date-fns";

const CATEGORIES = [
  { id: "opportunity", label: "Opportunity", color: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20", icon: Lightbulb },
  { id: "threat", label: "Threat", color: "text-rose-400 bg-rose-400/10 border-rose-400/20", icon: AlertTriangle },
  { id: "trend", label: "Trend", color: "text-blue-400 bg-blue-400/10 border-blue-400/20", icon: TrendingUp },
  { id: "community_buzz", label: "Community Buzz", color: "text-purple-400 bg-purple-400/10 border-purple-400/20", icon: Users },
];

function getCategoryData(cat: string) {
  return CATEGORIES.find(c => c.id === cat) || { id: "other", label: cat, color: "text-slate-400 bg-slate-400/10 border-slate-400/20", icon: Info };
}

export default function HarrySharedBrief() {
  const { token } = useParams();

  const { data, isLoading, error } = useQuery<{ 
    briefing: { briefingDate: string, createdAt: string }, 
    findings: HarryFinding[] 
  }>({
    queryKey: [`/api/public/harry/brief/${token}`],
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-sm text-muted-foreground font-medium uppercase tracking-widest">Loading Intel Brief...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    const status = (error as any)?.status;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center space-y-4 max-w-md">
          <div className="w-16 h-16 rounded-3xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mx-auto">
            <AlertTriangle className="text-rose-400" size={32} />
          </div>
          {status === 410 ? (
            <>
              <h1 className="text-2xl font-black uppercase tracking-tight">This brief has expired</h1>
              <p className="text-sm text-muted-foreground">This intelligence brief was generated on {data?.briefing?.createdAt ? format(new Date(data.briefing.createdAt), 'PP') : 'an earlier date'} and is no longer available.</p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-black uppercase tracking-tight">This link is invalid</h1>
              <p className="text-sm text-muted-foreground">The intelligence brief you're looking for could not be found or the link has been revoked.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const { briefing, findings } = data;
  const grouped = CATEGORIES.map(cat => ({
    ...cat,
    items: findings.filter(f => f.category === cat.id)
  })).filter(g => g.items.length > 0);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans selection:bg-blue-500/30">
      <div className="max-w-4xl mx-auto px-6 py-12 lg:py-20">
        {/* Header */}
        <header className="mb-16 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-blue-600 flex items-center justify-center shadow-xl shadow-blue-500/20">
              <Radar size={20} className="text-white" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-500">Intelligence Briefing</p>
              <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tighter">
                {format(new Date(briefing.briefingDate), 'MMMM do, yyyy')}
              </h1>
            </div>
          </div>
          <div className="h-px w-full bg-gradient-to-r from-border/60 via-border/20 to-transparent" />
        </header>

        {/* Findings List */}
        <main className="space-y-16">
          {grouped.map(group => (
            <section key={group.id} className="space-y-8">
              <div className="flex items-center gap-3">
                <group.icon className={group.color.split(' ')[0]} size={18} />
                <h2 className="text-sm font-black uppercase tracking-widest text-muted-foreground">
                  {group.label}
                </h2>
                <Badge variant="outline" className={`${group.color} border-none font-black`}>
                  {group.items.length}
                </Badge>
              </div>

              <div className="grid grid-cols-1 gap-8">
                {group.items.map(finding => (
                  <article key={finding.id} className="group relative">
                    <div className="absolute -left-6 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500/0 via-blue-500/40 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="text-xl font-bold leading-tight group-hover:text-blue-400 transition-colors">
                          {finding.title}
                        </h3>
                        <a 
                          href={finding.sourceUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 rounded-xl bg-muted/30 border border-border/40 hover:border-blue-500/40 hover:bg-blue-500/5 text-muted-foreground/40 hover:text-blue-400 transition-all shrink-0"
                        >
                          <ExternalLink size={16} />
                        </a>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {finding.summary}
                      </p>
                      <div className="flex items-center gap-4 pt-2">
                        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">
                          <Clock size={10} />
                          {finding.sourcePublishedAt ? format(new Date(finding.sourcePublishedAt), 'h:mm a') : 'Recently'}
                        </div>
                        <span className="text-border/40 text-xs">·</span>
                        <div className="text-[10px] font-black uppercase tracking-widest text-blue-500/40">
                          {finding.sourceType} / {finding.sourceIdentifier}
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </main>

        {/* Footer */}
        <footer className="mt-24 pt-12 border-t border-border/40 text-center space-y-4">
          <div className="flex items-center justify-center gap-2 opacity-30 grayscale contrast-125">
             <Radar size={16} />
             <span className="text-xs font-black uppercase tracking-widest">Harry · Draper.ai</span>
          </div>
          <p className="text-[10px] text-muted-foreground/30 font-medium">
            Confidential Scout Intelligence Brief · &copy; {new Date().getFullYear()} Draper AI
          </p>
        </footer>
      </div>
    </div>
  );
}
